/**
 * Which session a figure on the board actually belongs to.
 *
 * Volume and Open Interest sit side by side as though both were "now". They are not, and
 * the gap is not a rounding detail:
 *
 *   - Open interest is published ONCE, the morning after the session it describes. The
 *     figure shown therefore always describes the PREVIOUS session's close — during
 *     regular hours as much as before the open. It does not move intraday.
 *   - Volume is the current session's, but only once the session has started. Before the
 *     open it is the last completed session's, which looks identical to a quiet morning.
 *
 * Verified on SPXW 7785C: pre-market on 2026-08-25 the board showed volume 8,209 against
 * open interest 8,306. The 8,209 was 2026-08-24's volume; the 8,306 was the open interest
 * published that morning BECAUSE of it. Two different sessions, unlabelled.
 *
 * Holidays are deliberately not modelled — there is no exchange calendar on the client and
 * inventing one would be worse than the gap it closes. So nothing here names a date: it
 * says which session a figure belongs to, never which day that was.
 *
 * Separate from `marketSession.ts` on purpose: that module answers "is the market open" for
 * data-feed decisions. This one answers "what is this number as of", which is a labelling
 * question and needs an injectable clock so it can be tested.
 */

export type SessionPhase = 'preopen' | 'open' | 'afterhours' | 'weekend';

const OPEN_MINUTE = 9 * 60 + 30;
const CLOSE_MINUTE = 16 * 60;

/** Minutes past midnight in New York, and the weekday there. */
function newYorkClock(now: Date): { minutes: number; weekday: number } | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    const hour = Number(get('hour'));
    const minute = Number(get('minute'));
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weekday = days.indexOf(get('weekday'));
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || weekday < 0) return null;
    return { minutes: (hour % 24) * 60 + minute, weekday };   // midnight can format as 24
  } catch {
    return null;
  }
}

export function sessionPhase(now: Date = new Date()): SessionPhase {
  const clock = newYorkClock(now);
  // With no reliable clock, choose the phase that suppresses the fewest caveats: assuming
  // "open" would silently drop the "this is yesterday's volume" warning.
  if (clock === null) return 'preopen';
  if (clock.weekday === 0 || clock.weekday === 6) return 'weekend';
  if (clock.minutes < OPEN_MINUTE) return 'preopen';
  if (clock.minutes <= CLOSE_MINUTE) return 'open';
  return 'afterhours';
}

/** What the Volume column is showing, or null when it needs no caveat. */
export function volumeAsOfNote(phase: SessionPhase): string | null {
  if (phase === 'open') return null;
  return 'the last completed session — today has not traded yet';
}

/** What the Open Interest column is showing. True in every phase, including during RTH. */
export const OPEN_INTEREST_NOTE =
  "the last published figure, which describes the previous session's close — open interest "
  + 'is published once, after the close, so it never moves intraday';
